import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DashboardCard,
  PageHeader,
  cn,
} from '@zafroi/ui-kit';
import {
  ArrowLeft,
  ArrowRight,
  Compass,
  Info,
  Lightbulb,
  ShieldAlert,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  CalloutTone,
  GuideBlock,
  GuideSection,
} from '@/modules/chatbot/guideContent';
import { CONVERSATIONS_GUIDE_SECTIONS } from './conversationsGuideContent';

/**
 * ConversationsGuidePage — the owner/agent-facing "how does the unified
 * inbox work?" reference. Lives at `/conversations/guide` and is linked
 * from the Conversation Inbox + Detail pages (Help & Guide button).
 *
 * Mirrors the layout of `ChatbotGuidePage` exactly — sticky TOC, reading-
 * width body, the same visual language for callouts and CTAs. The block
 * renderer is intentionally duplicated rather than lifted to a shared
 * component because the two guides are likely to diverge visually over
 * time (e.g. Conversations may grow channel-specific blocks) and shipping
 * a premature abstraction would constrain that. The block *types*, on the
 * other hand, are imported from `chatbot/guideContent` so the data shape
 * stays in lockstep.
 */
export function ConversationsGuidePage() {
  const location = useLocation();

  // When the user lands on `/conversations/guide#<id>`, scroll that section
  // into view after the page renders. Without this, the browser ignores the
  // hash because the anchor doesn't exist yet at navigation time.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = window.setTimeout(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/conversations">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Conversations
          </Link>
        </Button>
      </div>

      <PageHeader
        eyebrow="Guide"
        title="Conversations Guide"
        description="How the inbox works — for non-technical owners and support agents."
      />

      <GuideTableOfContents sections={CONVERSATIONS_GUIDE_SECTIONS} />

      <div className="mx-auto w-full max-w-[720px] space-y-10">
        {CONVERSATIONS_GUIDE_SECTIONS.map((section) => (
          <GuideSectionBlock key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
}

// ─── Table of contents ──────────────────────────────────────────────────────

function GuideTableOfContents({ sections }: { sections: GuideSection[] }) {
  return (
    <div className="mx-auto w-full max-w-[720px]">
      <DashboardCard
        icon={Compass}
        title="In this guide"
        description="Jump straight to the part you need."
      >
        <ul className="grid gap-1.5 sm:grid-cols-2">
          {sections.map((section, index) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                className="group flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm text-foreground transition-colors hover:border-border hover:bg-muted/50"
              >
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground group-hover:bg-primary/15 group-hover:text-primary">
                  {index + 1}
                </span>
                <span className="min-w-0 truncate">{section.title}</span>
              </a>
            </li>
          ))}
        </ul>
      </DashboardCard>
    </div>
  );
}

// ─── Section ────────────────────────────────────────────────────────────────

function GuideSectionBlock({ section }: { section: GuideSection }) {
  const navigate = useNavigate();
  const Icon = section.icon;
  return (
    <section
      id={section.id}
      // `scroll-mt` gives a small offset so the section title isn't flush with
      // the top of the viewport when a TOC anchor jump lands here.
      className="scroll-mt-24 space-y-4"
    >
      <div className="flex items-start gap-3">
        <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            {section.title}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {section.intro}
          </p>
        </div>
      </div>

      <div className="space-y-4 pl-12">
        {section.body.map((block, i) => (
          <GuideBlockRenderer key={i} block={block} />
        ))}
        {section.cta && (
          <div className="pt-1">
            <Button
              size="sm"
              onClick={() => {
                // React Router's navigate handles both pathname and hash, so
                // CTAs like `/support` or `/channels` work the same way.
                navigate(section.cta!.href);
              }}
            >
              {section.cta.label}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Blocks ─────────────────────────────────────────────────────────────────

function GuideBlockRenderer({ block }: { block: GuideBlock }) {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p className="text-sm leading-relaxed text-foreground/90">
          {block.text}
        </p>
      );

    case 'bullets':
      return (
        <ul className="space-y-2 text-sm leading-relaxed text-foreground/90">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2.5">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60"
              />
              <span>
                {item.label && (
                  <span className="font-semibold text-foreground">
                    {item.label}{' '}
                  </span>
                )}
                {item.text}
              </span>
            </li>
          ))}
        </ul>
      );

    case 'numbered':
      return (
        <ol className="space-y-3 text-sm leading-relaxed text-foreground/90">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[12px] font-semibold text-primary">
                {i + 1}
              </span>
              <span>
                {item.label && (
                  <span className="font-semibold text-foreground">
                    {item.label}{' '}
                  </span>
                )}
                {item.text}
              </span>
            </li>
          ))}
        </ol>
      );

    case 'callout':
      return <GuideCallout {...block} />;

    case 'grid':
      return (
        <div className="overflow-hidden rounded-md border border-border">
          <div className="grid grid-cols-[1fr_auto] gap-x-4 bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span>{block.headers[0]}</span>
            <span>{block.headers[1]}</span>
          </div>
          <ul className="divide-y divide-border">
            {block.rows.map((row) => (
              <li
                key={row.name}
                className="grid grid-cols-[1fr_auto] items-start gap-x-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {row.name}
                  </div>
                  {row.description && (
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {row.description}
                    </p>
                  )}
                </div>
                <Badge
                  tone={statusToneForChannel(row.status)}
                  withDot={false}
                  className="shrink-0"
                >
                  {row.status}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'qa':
      return (
        <div className="divide-y divide-border rounded-md border border-border">
          {block.items.map((qa, i) => (
            <details
              key={i}
              className="group [&_summary::-webkit-details-marker]:hidden"
              // First Q open by default so the user immediately sees the
              // FAQ shape, matching the Chatbot guide's behaviour.
              open={i === 0}
            >
              <summary className="flex cursor-pointer items-start justify-between gap-3 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/40">
                <span>
                  <span className="text-primary">Q:</span> {qa.question}
                </span>
                <span
                  aria-hidden="true"
                  className="mt-1 inline-block h-2 w-2 shrink-0 rotate-45 border-r border-b border-muted-foreground/60 transition-transform group-open:-rotate-45"
                />
              </summary>
              <div className="px-4 pb-4 text-sm leading-relaxed text-foreground/90">
                <span className="font-semibold text-muted-foreground">A: </span>
                {qa.answer}
              </div>
            </details>
          ))}
        </div>
      );

    default: {
      // Exhaustiveness guard — assigning to `never` makes TS surface a new
      // unhandled `block.kind` at compile time. We still need a runtime
      // value for the renderer.
      const _exhaustive: never = block;
      void _exhaustive;
      return null;
    }
  }
}

// ─── Callout ────────────────────────────────────────────────────────────────

const CALLOUT_STYLES: Record<
  CalloutTone,
  { icon: LucideIcon; border: string; iconBg: string; iconColor: string }
> = {
  tip: {
    icon: Lightbulb,
    border: 'border-l-info',
    iconBg: 'bg-info/15',
    iconColor: 'text-info',
  },
  important: {
    icon: ShieldAlert,
    border: 'border-l-warning',
    iconBg: 'bg-warning/15',
    iconColor: 'text-warning',
  },
  note: {
    icon: Info,
    border: 'border-l-muted-foreground/40',
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
  },
};

function GuideCallout({
  tone,
  title,
  text,
}: {
  tone: CalloutTone;
  title?: string;
  text: string;
}) {
  const styles = CALLOUT_STYLES[tone];
  const Icon = styles.icon;
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border border-border border-l-4 bg-card px-4 py-3 text-sm leading-relaxed text-foreground/90 shadow-elev-1',
        styles.border,
      )}
    >
      <span
        className={cn(
          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
          styles.iconBg,
          styles.iconColor,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        {title && (
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {title}
          </div>
        )}
        <p className="mt-0.5">{text}</p>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Map a roadmap-status string to a Badge tone — "Live today" reads as a
 * success, "Coming next" as info, anything else as muted. Kept here in
 * sync with the chatbot guide so the two render identically if a future
 * Conversations grid uses the same status vocabulary.
 */
function statusToneForChannel(
  status: string,
): 'success' | 'info' | 'neutral' | 'warning' {
  const lower = status.toLowerCase();
  if (lower.includes('live')) return 'success';
  if (lower.includes('coming')) return 'info';
  return 'neutral';
}
